import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { getIsTrainIncident } from '@rsos/base-utils';
import { CAPSTONE } from '@rsos/constants/adminContents';
import {
  DISPATCH_REQUESTED,
  IGNORED,
  NEW,
  DECLINED,
  ACCEPTED,
} from '@rsos/constants/alertStatuses';
import { ACTIVE_ASSAILANT } from '@rsos/constants/alertTypes';
import postMidasFlow from '@rsos/midas/src/postMidasFlow';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import { MW_5_0_0 } from '../../constants/migrationVersions';
import {
  getAlerts,
  getAlert,
  getEmergencyTypes,
  getAlertTypes,
  postAlertFlow,
  patchAlertStatus,
} from './alertsAPI';

const convertArrayToObject = (array, key) => {
  const initialValue = {};
  return array
    ? array.reduce((obj, item) => {
        return {
          ...obj,
          [item[key]]: item,
        };
      }, initialValue)
    : {};
};

/**
 * Normalize alerts for use with badge notification counters and map markers
 * @param {Array} alerts - list of alerts from fetching all alerts
 */
const normalizeAlerts = alerts => {
  return alerts.map(alert => {
    if (!alert.isIgnoredByUserID) {
      alert.isIgnoredByUserID = '';
    }
    if (!alert.isStatusUpdated) {
      alert.isStatusUpdated = false;
      alert.numStatusUpdates = 0;
    }
    if (!alert.isUnread) {
      alert.isUnread = false;
      alert.numUnreads = 0;
    }
    if (!alert.location_history) {
      alert.location_history = Object.keys(alert.location).length
        ? [{ ...alert.location, created_at: alert.created_time }]
        : [];
    }
  });
};

const addExistingWhat3Words = (existingAlert, fetchedAlert) => {
  // If the alert apis dont align or alert doesn't exist, just skip this entirely
  if (
    !existingAlert ||
    existingAlert.location_history?.length !==
      fetchedAlert.location_history?.length
  ) {
    return fetchedAlert;
  }

  existingAlert.location_history.forEach((location, index) => {
    fetchedAlert.location_history[index].what3words = location.what3words;
  });

  return fetchedAlert;
};

// The status at which an alert has just arrived and awaits action. In RSP,
// DISPATCH_REQUESTED is treated as a NEW alert.
const getArrivalStatus = currentApplication =>
  currentApplication === CAPSTONE ? DISPATCH_REQUESTED : NEW;

// An arrived alert should not display the unread bar on the left side of the
// alert. Additionally, if an alert is currently selected, it should not display
// the unread bar.
const checkAlertStatus = (
  alertID,
  selectedAlertID,
  messageName,
  currentApplication,
) => {
  return (
    alertID !== selectedAlertID &&
    messageName !== getArrivalStatus(currentApplication)
  );
};

// The alerts service serializes `message` as a JSON string (see
// rsos/libs/alerts/alert.py — LogMessageModelInterface.create json.dumps the
// dict). Parse defensively at the reducer boundary so downstream callers
// can read message.name without knowing about the wire format.
const parseLogMessage = message => {
  if (message && typeof message === 'object') return message;
  if (typeof message !== 'string') return {};
  try {
    return JSON.parse(message);
  } catch {
    return {};
  }
};

const updatedAlerts = (alerts, selectedAlertID, payload) => {
  const { alert_id: alertID, message, current_application } = payload;

  const updatedStatus = {
    display_name: message.display_name,
    name: message.name,
  };

  if (message.name === DISPATCH_REQUESTED) {
    return {
      ...alerts,
      [alertID]: {
        ...alerts[alertID],
        isStatusUpdated: checkAlertStatus(
          alertID,
          selectedAlertID,
          message.name,
          current_application,
        ),
        sla_expiration_time: payload.sla_expiration_time,
        status: updatedStatus,
      },
    };
  }
  if (message.name === ACCEPTED || message.name === DECLINED) {
    const enrichedStatus = {
      ...updatedStatus,
      sender: payload.sender,
    };
    return {
      ...alerts,
      [alertID]: {
        ...alerts[alertID],
        isStatusUpdated: checkAlertStatus(
          alertID,
          selectedAlertID,
          message.name,
          current_application,
        ),
        status: enrichedStatus,
      },
    };
  }

  return {
    ...alerts,
    [alertID]: {
      ...alerts[alertID],
      isStatusUpdated: checkAlertStatus(
        alertID,
        selectedAlertID,
        message.name,
        current_application,
      ),
      status: updatedStatus,
    },
  };
};

/**
 * - Set alertID as the selected alert ID.
 * - Set the selected alert ID as read.
 * - Removes the selected alert ID from unreadAlerts and audibleNotificationIDs.
 * @param {Object} state - The incidents state
 * @param {String} alertID - The alert ID
 */
const setSelectedAlert = (state, alertID) => {
  state.selectedAlertID = alertID;

  if (state.alerts[alertID] && state.alerts[alertID].isStatusUpdated) {
    state.alerts[alertID].isStatusUpdated = false;

    state.alerts[alertID].numStatusUpdates = 0;

    if (state.unreadAlerts.includes(a => a.alert_id === alertID)) {
      state.unreadAlerts = state.unreadAlerts.filter(
        alert => alert.alert_id !== alertID,
      );
    }
  }

  const notificationIDIndex = state.audibleNotificationIDs.indexOf(alertID);

  if (notificationIDIndex > -1) {
    state.audibleNotificationIDs = [
      ...state.audibleNotificationIDs.slice(0, notificationIDIndex),
      ...state.audibleNotificationIDs.slice(notificationIDIndex + 1),
    ];
  }
};

// Server-owned fields refreshed on an existing alert when the backfilled copy
// is newer. Local UI flags are deliberately excluded.
const BACKFILL_STATUS_FIELDS = [
  'status',
  'sla_expiration_time',
  'dispatch_requested_time',
  'last_updated_time',
];

/**
 * Adds an alert to the store the way a live websocket alert is added: normalizes
 * the location, sets the local UI flags, flags it for the new-alert
 * highlight/sound and indexes it into the active assailant / train / test maps.
 * @param {Object} state - The incidents state
 * @param {Object} alert - The alert payload from the server
 * @param {Object} [options]
 * @param {boolean} [options.notify=true] - When false the alert is added
 *   quietly: no sound, no new-alert highlight and no status-updated flag.
 */
const insertNewAlert = (state, alert, { notify = true } = {}) => {
  const { alert_id: alertID, emergency_type: emergencyType } = alert;

  const normalizedLocation =
    Object.keys(alert?.location).length > 0
      ? {
          ...alert.location,
          created_at: alert.created_time,
        }
      : {};

  const normalizedAlert = {
    ...alert,
    isIgnoredByUserID: '',
    isStatusUpdated: notify,
    isUnread: false,
    location: normalizedLocation,
    numStatusUpdates: notify ? 1 : 0,
    numUnreads: 0,
  };
  if (!state.alerts[alertID]) {
    state.alerts = {
      ...state.alerts,
      [alertID]: normalizedAlert,
    };
  } else {
    state.alerts[alertID] = {
      ...alert,
    };
  }
  if (notify) {
    state.IDWithNewAlert = alertID;
    state.IDWithSound = alertID;
  }
  if (emergencyType?.name === ACTIVE_ASSAILANT) {
    state.activeAssailantAlerts = {
      ...state.activeAssailantAlerts,
      [alertID]: normalizedAlert,
    };
  }
  if (getIsTrainIncident(emergencyType?.name, true)) {
    state.trainAlerts = {
      ...state.trainAlerts,
      [alertID]: normalizedAlert,
    };
  }
  if (emergencyType?.name.startsWith('TEST')) {
    state.testAlerts = {
      ...state.testAlerts,
      [alertID]: normalizedAlert,
    };
  }
};

/**
 * Applies the live outcome of an alert becoming IGNORED or DECLINED: flag the
 * id so the map markers drop it and remove it from the queue, unless it is the
 * currently selected alert.
 * @param {Object} state - The incidents state
 * @param {string} alertID - The alert ID
 * @param {string} statusName - The alert's new status name
 */
const removeIfIgnoredOrDeclined = (state, alertID, statusName) => {
  if (statusName === IGNORED) {
    state.ignoreAlertID = alertID;
    if (state.selectedAlertID !== alertID) {
      delete state.alerts[alertID];
    }
  }
  if (statusName === DECLINED) {
    state.declinedAlertID = alertID;
    if (alertID && state.selectedAlertID !== alertID) {
      delete state.alerts[alertID];
    }
  }
};

/**
 * Side effects shared by every path that moves an alert already in the store to
 * a new status: flags it as updated unless it is being viewed, counts the
 * audible notification, and stops its repeating sound.
 * @param {Object} state - The incidents state
 * @param {string} alertID - The alert ID, whose new status is already stored
 */
const applyAlertStatusChange = (state, alertID) => {
  if (state.selectedAlertID !== alertID) {
    state.alerts[alertID].isStatusUpdated = true;
    state.alerts[alertID].numStatusUpdates = 1;
    state.numAudibleNotifications += 1;
  } else {
    state.alerts[alertID].isStatusUpdated = false;
  }

  state.alertsWithRepeat.map(alert => {
    if (alert.alertID === alertID) {
      alert.status = state.alerts[alertID].status.name;
      alert.audio.loop = false;

      if (state.IDWithSound === alertID) {
        state.IDWithSound = null;
      }
    }
  });
};

/**
 * Puts a location at the front of an alert's location history, unless it is the
 * one already there. Shared by the live location update and the backfill merge.
 * @param {Array|undefined} history - The alert's current location_history
 * @param {Object} newLocation - The location, with created_at
 * @returns {Array} The new location_history
 */
const addToLocationHistory = (history, newLocation) => {
  if (!history) {
    return [newLocation];
  }
  const isDuplicate = newLocation.created_at === history[0]?.created_at;
  return isDuplicate ? [...history] : [newLocation, ...history];
};

/**
 * The location fields a newer server copy adds to a known alert: locations
 * newer than the newest one held, put into the history the same way a live
 * location update does. Empty when the server copy has nothing newer.
 * @param {Object} existing - The alert in the store
 * @param {Object} alert - The newer server copy
 */
const getBackfilledLocationFields = (existing, alert) => {
  const serverHistory = Array.isArray(alert.location_history)
    ? alert.location_history
    : [];
  const hasLocation = alert.location && Object.keys(alert.location).length > 0;
  const incoming = serverHistory.length
    ? serverHistory
    : hasLocation
      ? [
          {
            ...alert.location,
            created_at: alert.location.created_at ?? alert.last_updated_time,
          },
        ]
      : [];
  const heldNewest = existing.location_history?.[0]?.created_at;
  // The server lists newest first; add oldest first so the newest ends up first.
  const newer = incoming
    .filter(loc => heldNewest == null || loc.created_at > heldNewest)
    .reverse();
  if (!newer.length) {
    return {};
  }
  return {
    location: newer[newer.length - 1],
    location_history: newer.reduce(
      (history, loc) => addToLocationHistory(history, loc),
      existing.location_history,
    ),
  };
};

const isIgnoredOrDeclined = alert =>
  alert?.status?.name === IGNORED || alert?.status?.name === DECLINED;

/**
 * Merges alerts re-fetched after a websocket reconnect into the store without
 * replacing it: unknown alerts are added like live ones, known alerts keep
 * their local UI state and only take the server's status fields when the
 * server copy is newer. Alerts the user removed are never re-added.
 * A live update that arrives while the backfill request is in flight can be
 * overwritten by the older snapshot; this is accepted.
 * @param {Object} state - The incidents state
 * @param {Array} alerts - Alerts returned by the server
 * @param {Object} request
 * @param {string} [request.currentApplication] - Decides which statuses count
 *   as a newly arrived alert
 */
const mergeBackfilledAlerts = (state, alerts, { currentApplication }) => {
  [...alerts]
    .sort((a, b) => (a.created_time ?? 0) - (b.created_time ?? 0))
    .forEach(alert => {
      const alertID = alert?.alert_id;
      if (!alertID || state.removedAlerts.includes(alertID)) {
        return;
      }
      const existing = state.alerts[alertID];
      if (!existing) {
        // Already handled during the gap: the live path would have removed it.
        if (isIgnoredOrDeclined(alert)) {
          return;
        }
        // Same shape as a fetched alert (location_history, defaults) so the
        // detail views treat it alike. insertNewAlert still decides the
        // notification flags.
        const normalized = { ...alert };
        normalizeAlerts([normalized]);
        // Only alerts still awaiting action get the new-alert sound/highlight.
        insertNewAlert(state, normalized, {
          notify: alert.status?.name === getArrivalStatus(currentApplication),
        });
        return;
      }
      if ((alert.last_updated_time ?? 0) <= (existing.last_updated_time ?? 0)) {
        return;
      }
      const statusChanged = existing.status?.name !== alert.status?.name;
      const statusFields = {
        ...BACKFILL_STATUS_FIELDS.reduce(
          (fields, key) =>
            key in alert ? { ...fields, [key]: alert[key] } : fields,
          {},
        ),
        ...getBackfilledLocationFields(existing, alert),
      };
      ['alerts', 'activeAssailantAlerts', 'trainAlerts', 'testAlerts'].forEach(
        mapKey => {
          if (state[mapKey][alertID]) {
            state[mapKey][alertID] = {
              ...state[mapKey][alertID],
              ...statusFields,
            };
          }
        },
      );
      if (statusChanged) {
        applyAlertStatusChange(state, alertID);
      }
      removeIfIgnoredOrDeclined(state, alertID, alert.status?.name);
    });
};

export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async ({ orgName, queueMaxSize, endTime }, { rejectWithValue }) => {
    // The startTime (currently unused) can be used to support infinite
    // scrolling / pagination from the UI by triggering a request when
    // scrolling to the end of the queue using the `incidents.alerts_until` value
    try {
      const response = await getAlerts(orgName, queueMaxSize, endTime);
      return response;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

// Re-fetches the alert queue after a websocket reconnect. Unlike fetchAlerts the
// result is merged into the store, so it never touches `alertsUntil` or drops
// alerts that arrived over the websocket in the meantime.
export const backfillAlerts = createAsyncThunk(
  'alerts/backfillAlerts',
  async ({ orgName, queueMaxSize, endTime }, { rejectWithValue }) => {
    try {
      const response = await getAlerts(orgName, queueMaxSize, endTime);
      const pages = Array.isArray(response) ? response : [response];
      return pages.flatMap(page => page?.alerts ?? []);
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const fetchAlert = createAsyncThunk(
  'alerts/fetchAlert',
  async (
    { currentApplication, currentOrgName, alertID, shouldSetID },
    { rejectWithValue },
  ) => {
    // `applicationName` is used to determine if the request is coming from
    // CAPSTONE or CENTRAL_STATION. It is used when the request is fulfilled.
    try {
      const response = await getAlert(currentOrgName, alertID);
      return { alert: response.data, currentApplication, shouldSetID };
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

// Re-fetches one alert and merges it into the store like a backfill: newer
// server copies only, local UI state kept. Unlike fetchAlert it sets no loading
// flag, so a screen that swaps itself for a loader while loading.alert is set
// is not torn down by a background refresh.
export const refreshAlert = createAsyncThunk(
  'alerts/refreshAlert',
  async ({ currentOrgName, alertID }, { rejectWithValue }) => {
    try {
      const response = await getAlert(currentOrgName, alertID);
      return response.data;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const fetchEmergencyTypes = createAsyncThunk(
  'alerts/fetchEmergencyTypes',
  async (orgName, { rejectWithValue }) => {
    // The startTime (currently unused) can be used to support infinite
    // scrolling / pagination from the UI by triggering a request when
    // scrolling to the end of the queue using the `incidents.alerts_until` value
    try {
      const response = await getEmergencyTypes(orgName);
      return response;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const fetchAlertTypes = createAsyncThunk(
  'alerts/fetchAlertTypes',
  async (orgName, { rejectWithValue }) => {
    try {
      const response = await getAlertTypes(orgName);
      return response;
    } catch (error) {
      const { message } = normalizeError(error);
      const errorMessage = message || 'Error fetching alert types';
      return rejectWithValue(errorMessage);
    }
  },
);

export const triggerAlertFlow = createAsyncThunk(
  'alerts/triggerAlertFlow',
  async (
    {
      callflow,
      blocking = false,
      test_mode = false,
      variables = {},
      fetchConfig,
    },
    { getState, dispatch, rejectWithValue },
  ) => {
    try {
      const response = await postAlertFlow({
        callflow,
        blocking,
        test_mode,
        variables,
        getState,
        dispatch,
        fetchConfig,
      });

      return response;
    } catch (error) {
      const newError = await error.json();
      return rejectWithValue(newError.detail);
    }
  },
);

export const triggerMidasFlow = createAsyncThunk(
  'alerts/triggerMidasFlow',
  async ({ callflow, variables = {} }, { rejectWithValue }) => {
    try {
      const response = await postMidasFlow({
        callflow,
        variables,
      });

      return response;
    } catch (error) {
      const { message } = normalizeError(error);
      return rejectWithValue(message);
    }
  },
);

export const updateAlertStatus = createAsyncThunk(
  'alerts/updateAlertStatus',
  async (info, { rejectWithValue }) => {
    try {
      const response = await patchAlertStatus(info);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const initialState = {
  activeAlertToasts: [],
  alerts: {},
  audibleNotificationIDs: [],
  declinedAlertID: null,
  error: {
    allAlerts: null,
    alert: null,
    alertAdr: null,
    alertFlow: null,
    emergencyTypes: null,
    alertStatus: null,
    alertTypes: null,
  },
  IDWithNewAlert: null, // Note: this isn't persisted like numberWithNewLocation
  IDWithSound: null,
  loading: {
    allAlerts: false,
    alert: false,
    alertAdr: false,
    emergencyTypes: false,
    alertFlow: false,
    alertStatus: false,
    alertTypes: false,
  },
  numUnreadAlertNotifications: 0,
  removedAlerts: [],
  // Set whenever a backfill merges, so map markers redraw from the alerts map.
  alertsBackfilledAt: 0,
  selectedAlertID: null,
  unreadAlerts: [],
  alertsWithRepeat: [],
  alertsQueueOrder: [],
  latestMsgTime: 0,
  activeAssailantAlerts: {},
  trainAlerts: {},
  testAlerts: {},
  emergencyTypes: [],
  alertTypes: [],
};

const incidentsSlice = createSlice({
  name: 'incidents',
  initialState: initialState,
  reducers: {
    addNewAlert: (state, action) => {
      insertNewAlert(state, action.payload);
    },
    addFollower: (state, action) => {
      if (
        state.selectedAlertID &&
        !state.alerts[state.selectedAlertID].followers.includes(action.payload)
      ) {
        state.alerts[state.selectedAlertID].followers.push(action.payload);
      }
    },
    receivedAlertUpdatedStatus: (state, action) => {
      const alertID = action.payload.alert_id;
      const message = parseLogMessage(action.payload.message);
      const normalizedPayload = { ...action.payload, message };
      if (normalizedPayload.message_type === 'STATUS_UPDATE') {
        state.alerts = updatedAlerts(
          state.alerts,
          state.selectedAlertID,
          normalizedPayload,
        );
        state.activeAssailantAlerts = updatedAlerts(
          state.activeAssailantAlerts,
          state.selectedAlertID,
          normalizedPayload,
        );
        state.trainAlerts = updatedAlerts(
          state.trainAlerts,
          state.selectedAlertID,
          normalizedPayload,
        );
        state.testAlerts = updatedAlerts(
          state.testAlerts,
          state.selectedAlertID,
          normalizedPayload,
        );
        applyAlertStatusChange(state, alertID);
      }
      removeIfIgnoredOrDeclined(state, alertID, message.name);
    },
    removeAlert: (state, action) => {
      if (action.payload) {
        state.alertsWithRepeat.map(alert => {
          if (alert.alertID === action.payload) {
            alert.audio.loop = false;
            if (state.IDWithSound === action.payload) {
              state.IDWithSound = null;
            }
          }
        });

        delete state.alerts[action.payload];
        if (state.activeAssailantAlerts[action.payload]) {
          delete state.activeAssailantAlerts[action.payload];
        }
        if (state.trainAlerts[action.payload]) {
          delete state.trainAlerts[action.payload];
        }
        if (state.testAlerts[action.payload]) {
          delete state.testAlerts[action.payload];
        }
        if (action.payload === state.selectedAlertID) {
          state.selectedAlertID = null;
        }
        state.removedAlerts = [...state.removedAlerts, action.payload];

        const alertIdx = state.alertsQueueOrder.findIndex(
          a => a.alertID === action.payload,
        );
        if (alertIdx !== -1) {
          state.alertsQueueOrder.splice(alertIdx, 1);
        }
      }
    },
    resetIDWithNewAlert: state => {
      state.IDWithNewAlert = null;
    },
    resetIDWithSound: state => {
      state.IDWithSound = null;
    },
    resetMidasError: state => {
      state.error.alertFlow = null;
    },
    setSelectedAlertID: (state, action) => {
      const alertID = action.payload;

      setSelectedAlert(state, alertID);
    },
    updateAlert: (state, action) => {
      const alertID = action.payload.body.alert_id;
      // If alert was removed from queue, don't do anything
      if (state.removedAlerts.includes(alertID)) {
        return state;
      }
      const newLocation = {
        ...action.payload.body,
        created_at: action.payload.created_time,
      };
      delete newLocation.alert_id;
      // Note: When not viewing an alert, the `location_history` may not
      // necessarily match the alert's full `location_history`. The fetch all
      // alerts request only returns the most recent location and not the
      // history

      // compare newLocation with most recent loc to prevent duplication
      const isDuplicatedNewLocation =
        newLocation.created_at ===
        state.alerts[alertID]?.location_history?.[0]?.created_at;

      const locationHistory = addToLocationHistory(
        state.alerts[alertID].location_history,
        newLocation,
      );

      state.alerts[alertID] = {
        ...state.alerts[alertID],
        location: newLocation,
        location_history: locationHistory,
      };

      // Only update IDWithNewAlert when viewing it and !isDuplicatedNewLocation to prevent map marker issues
      if (
        (state.selectedAlertID === alertID || state.selectedAlertID === null) &&
        !isDuplicatedNewLocation
      ) {
        state.IDWithNewAlert = alertID;
      }
    },
    viewAllAlerts: state => {
      state.selectedAlertID = null;
      state.IDWithNewAlert = null;
    },
    setAlertUnread: (state, action) => {
      const alert = action.payload;
      const alertID = alert.alert_id;

      if (state.selectedAlertID !== alertID) {
        // Set the alert in the queue as unread
        state.alerts[alertID].isUnread = true;
        state.alerts[alertID].numUnreads = 1;

        const doesAlertExist = !!state.unreadAlerts.find(
          alert => alert.alert_id === alertID,
        );

        if (!doesAlertExist) {
          // Alert markers rely on state.unreadAlerts to determine if it should be unread or not
          state.unreadAlerts.push(state.alerts[alertID]);
        }
      }
    },
    setAlertRead: (state, action) => {
      const alertID = action.payload;

      if (state.alerts[alertID] && state.selectedAlertID === alertID) {
        if (state.alerts[alertID].isUnread) {
          state.alerts[alertID].isUnread = false;
          state.alerts[alertID].numUnreads = 0;
        }

        state.unreadAlerts = state.unreadAlerts.filter(
          alert => alert.alert_id !== alertID,
        );
      }
    },
    resetUnreadAlerts: state => {
      state.unreadAlerts = [];
    },
    setAlertQueueOrder: (state, action) => {
      const alertsQueue = action.payload;

      state.alertsQueueOrder = alertsQueue.filter(
        alert => alert.alertID !== state.selectedAlertID,
      );
    },
    removeAlertQueueOrder: (state, action) => {
      const alertID = action.payload;
      const alertIdx = state.alertsQueueOrder.findIndex(
        a => a.alertID === alertID,
      );
      if (alertIdx !== -1) {
        state.alertsQueueOrder.splice(alertIdx, 1);
      }
    },
    setLatestMsgTime: (state, action) => {
      const latestMsgTime = action.payload;
      if (state.latestMsgTime < latestMsgTime) {
        state.latestMsgTime = latestMsgTime;
      }
    },
    getNumUnreadAlertNotifications: state => {
      let totalNotifications = 0;
      if (Object.keys(state.alerts).length) {
        Object.values(state.alerts).forEach(alert => {
          let notification = 0;
          if (alert.numStatusUpdates || alert.numUnreads) notification = 1;
          totalNotifications += notification;
        });
      }
      state.numUnreadAlertNotifications = totalNotifications;
    },
    setAudibleNotificationIDs: (state, action) => {
      const alertID = action.payload;
      if (
        state.selectedAlertID !== alertID &&
        !state.audibleNotificationIDs.includes(alertID)
      ) {
        state.audibleNotificationIDs.push(alertID);
      }
    },
    setAlertIgnoredBy: (state, action) => {
      const { alertID, userID } = action.payload;
      state.alerts[alertID].isIgnoredByUserID = userID;
    },
    setAlertsWithRepeat: (state, action) => {
      const { alertID } = action.payload;
      if (action.payload) {
        const payloadWithStatus = {
          ...action.payload,
          status: state?.alerts[alertID]?.status.name,
        };
        state.alertsWithRepeat = [payloadWithStatus, ...state.alertsWithRepeat];
      }
    },
    addActiveAlertToasts: (state, action) => {
      state.activeAlertToasts = [...state.activeAlertToasts, action.payload];
    },
    removeActiveAlertToasts: (state, action) => {
      state.activeAlertToasts = state.activeAlertToasts.filter(
        i => i.alertID !== action.payload,
      );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchAlerts.pending, state => {
        state.loading.allAlerts = true;
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading.allAlerts = false;
        state.error.allAlerts = action.payload;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        let allAlerts = [];
        let alertsUntil;
        // If action.payload is an array, there were multiple requests to the
        // alerts endpoint
        if (Array.isArray(action.payload)) {
          action.payload.forEach(payload => {
            allAlerts = allAlerts.concat(payload.alerts);
          });
          alertsUntil = action.payload[action.payload.length - 1].alerts_until;
          // Take the last alerts_until value
        } else if (action.payload.alerts) {
          allAlerts = action.payload.alerts;
          alertsUntil = action.payload.alerts_until;
        }

        // Splice the alerts if number of alerts exceeds the queue max size
        const queueMaxSize = Number(action.meta.arg.queueMaxSize);
        if (allAlerts.length > queueMaxSize) {
          allAlerts = allAlerts.splice(0, queueMaxSize);
        }

        if (allAlerts.length) {
          normalizeAlerts(allAlerts);
          ((state.loading.allAlerts = false),
            (state.alerts = convertArrayToObject(allAlerts, 'alert_id')),
            (state.alertsUntil = alertsUntil));

          state.activeAssailantAlerts = convertArrayToObject(
            allAlerts.filter(
              a =>
                a.emergency_type.name === ACTIVE_ASSAILANT &&
                !state.removedAlerts.find(ra => a.alert_id === ra),
            ),
            'alert_id',
          );
          state.trainAlerts = convertArrayToObject(
            allAlerts.filter(
              a =>
                getIsTrainIncident(a.emergency_type.name, true) &&
                !state.removedAlerts.find(ra => a.alert_id === ra),
            ),
            'alert_id',
          );

          state.testAlerts = convertArrayToObject(
            allAlerts.filter(
              a =>
                a.emergency_type.name.startsWith('TEST') &&
                !state.removedAlerts.find(ra => a.alert_id === ra),
            ),
            'alert_id',
          );
        } else {
          state.loading.allAlerts = false;
          state.alerts = {};
          state.activeAssailantAlerts = {};
          state.trainAlerts = {};
          state.testAlerts = {};
          state.selectedAlertID = null;
          state.IDWithNewAlert = null;
        }
      })
      .addCase(backfillAlerts.fulfilled, (state, action) => {
        state.alertsBackfilledAt = Date.now();
        mergeBackfilledAlerts(state, action.payload, {
          currentApplication: action.meta.arg.currentApplication,
        });
      })
      .addCase(refreshAlert.fulfilled, (state, action) => {
        mergeBackfilledAlerts(state, [action.payload], {
          currentApplication: action.meta.arg.currentApplication,
        });
      })
      .addCase(fetchAlert.pending, state => {
        state.loading.alert = true;
      })
      .addCase(fetchAlert.rejected, (state, action) => {
        state.loading.alert = false;
        state.error.alert = action.payload;
      })
      .addCase(fetchAlert.fulfilled, (state, action) => {
        state.error.alert = null;
        state.loading.alert = false;
        const { alert, currentApplication } = action.payload;
        const alertID = alert.alert_id;
        const fetchedAlert = addExistingWhat3Words(
          state.alerts[alertID],
          alert,
        );
        // If the request is coming from CAPSTONE, add the alert to the alerts object when the alert
        // status is not 'NEW'. This is because when the status is 'NEW' it means that it has been
        // received by CENTRAL_STATION but not yet dispatched to CAPSTONE yet.
        // If the request is coming from CENTRAL_STATION, add the alert to the alerts object.
        if (currentApplication === CAPSTONE) {
          if (alert.status.name !== NEW) {
            state.alerts[alertID] = {
              ...fetchedAlert,
              isIgnoredByUserID: '',
              isStatusUpdated: alert?.isStatusUpdated ? true : false,
              isUnread: alert?.isUnread ? true : false,
              numStatusUpdates: alert?.isStatusUpdated ? 1 : 0,
              numUnreads: alert?.isUnread ? 1 : 0,
            };
          }
        } else {
          state.alerts[alertID] = {
            ...fetchedAlert,
            isIgnoredByUserID: '',
            isStatusUpdated: alert?.isStatusUpdated ? true : false,
            isUnread: alert?.isUnread ? true : false,
            numStatusUpdates: alert?.isStatusUpdated ? 1 : 0,
            numUnreads: alert?.isUnread ? 1 : 0,
          };
        }
        if (action.payload.shouldSetID) {
          setSelectedAlert(state, alertID);
        }
      })
      .addCase(fetchEmergencyTypes.pending, state => {
        state.loading.emergencyTypes = true;
      })
      .addCase(fetchEmergencyTypes.rejected, (state, action) => {
        state.loading.emergencyTypes = false;
        state.error.emergencyTypes = action.payload;
      })
      .addCase(fetchEmergencyTypes.fulfilled, (state, action) => {
        state.error.emergencyTypes = null;
        state.loading.emergencyTypes = false;
        state.emergencyTypes = action.payload.data?.emergency_types || [];
      })
      .addCase(fetchAlertTypes.pending, state => {
        state.loading.alertTypes = true;
      })
      .addCase(fetchAlertTypes.rejected, (state, action) => {
        state.loading.alertTypes = false;
        state.error.alertTypes = action.payload;
      })
      .addCase(fetchAlertTypes.fulfilled, (state, action) => {
        state.error.alertTypes = null;
        state.loading.alertTypes = false;
        state.alertTypes = action.payload.data?.alert_types || [];
      })
      .addCase(triggerAlertFlow.pending, state => {
        state.loading.alertFlow = true;
      })
      .addCase(triggerAlertFlow.rejected, (state, action) => {
        state.loading.alertFlow = false;
        state.error.alertFlow = action.payload;
      })
      .addCase(triggerAlertFlow.fulfilled, state => {
        state.loading.alertFlow = false;
        state.error.alertFlow = null;
      })
      .addCase(updateAlertStatus.pending, state => {
        state.loading.alertStatus = true;
      })
      .addCase(updateAlertStatus.fulfilled, state => {
        state.loading.alertStatus = false;
      })
      .addCase(updateAlertStatus.rejected, (state, action) => {
        state.loading.alertStatus = false;
        state.error.alertStatus = action.payload;
      })
      .addCase('emergencyCalls/viewAllCalls', state => {
        state.selectedAlertID = null;
      })
      .addCase('emergencyCalls/setSelectedCallerID', (state, action) => {
        // If payload has a truthy value, set selectedAlertID to null
        if (action.payload) {
          state.selectedAlertID = null;
        }
      })
      .addCase('interagency/setSelectedInteragencyChat', (state, action) => {
        if (action.payload) state.selectedAlertID = null;
      })
      .addCase('FORCED_LOGOUT_SUCCESS', () => {
        return initialState;
      })
      .addCase('LOGOUT_SUCCESS', () => {
        return initialState;
      })
      .addCase('SINATRA_LOGOUT', () => {
        return initialState;
      })
      .addCase('APP_INIT_SUCCESS', state => {
        // Reset the selectedAlertID if the user was viewing an alert and their
        // permissions for Alerts was turned off or if the `selectedAlertID`
        // doesn't exist in `alerts`
        if (
          !Object.keys(state.alerts).length ||
          !state.alerts[state.selectedAlertID]
        ) {
          state.selectedAlertID = null;
        }
      })
      .addCase('app/initialize/rejected', state => {
        state.selectedAlertID = null;
      })
      .addCase('APP_INIT_START', state => {
        // emergency_data uses `APP_INIT_START`,
        return state;
      })
      .addCase('app/initialize/pending', state => {
        // central_station uses `app/initialize/pending`
        return state;
      })
      .addCase('DECRYPT_IRP_TOKEN_SUCCESS', (state, action) => {
        state.selectedAlertID = action.data.query.split('//')[1];
      });
  },
});

export const {
  addNewAlert,
  addFollower,
  getNumUnreadAlertNotifications,
  receivedAlertUpdatedStatus,
  removeAlert,
  resetIDWithNewAlert,
  resetIDWithSound,
  resetUnreadAlerts,
  resetMidasError,
  setAlertIgnoredBy,
  setAlertsWithRepeat,
  setAlertRead,
  setAlertUnread,
  setAlertQueueOrder,
  removeAlertQueueOrder,
  setLatestMsgTime,
  setAudibleNotificationIDs,
  setSelectedAlertID,
  updateAlert,
  viewAllAlerts,
  addActiveAlertToasts,
  removeActiveAlertToasts,
} = incidentsSlice.actions;

export const sortedAlertsByTime = state =>
  Object.values(state.incidents.alerts).sort(
    (a, b) => b.incident_time - a.incident_time,
  );

const persistConfig = {
  key: 'incidents',
  storage,
  version: MW_5_0_0,
  blacklist: ['alerts', 'IDWithNewAlert', 'alertsBackfilledAt'],
};

export const incidentsReducer = incidentsSlice.reducer;

const persistedReducer = persistReducer(persistConfig, incidentsReducer);

export default persistedReducer;
